import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import Modal from '@orn/shared-web/components/Modal/Modal';
import { ChevronDownIcon, ChevronUpIcon } from '@orn/shared-web/icons';
import formatTimeAgo from '@orn/shared-web/utils/datetime/formatTimeAgo';
import * as React from 'react';

import AdminSelectUser, {
  type IAdminSelectUserSelection,
  useSeedSelectionFromLink,
} from '../../components/admin/AdminSelectUser';
import Button from '../../components/Button';
import Header from '../../components/Header';
import { useToast } from '../../components/Toast';

import { ADMIN_GET_FINANCIAL_ACCOUNT_DETAIL_OPERATION } from './AccountDetailPanel';
import AccountsTable, { type AccountDetailRow } from './AccountsTable';
import {
  INVESTMENTS_PRODUCT,
  INVESTMENT_TRANSACTIONS_REQUEST_FAILED_MESSAGE,
  describeHoldingsSyncOutcome,
  describeInvestmentTransactionsSyncOutcome,
  hasInvestmentAccount,
  isInvestmentsRelinkCandidate,
} from './bankConnectionsView';

import type {
  AdminDeleteBankConnectionMutation,
  AdminDeleteBankConnectionMutationVariables,
  AdminGetFamilyBankConnectionsQuery,
  AdminGetFamilyBankConnectionsQueryVariables,
  AdminGetFinancialAccountDetailsQuery,
  AdminGetFinancialAccountDetailsQueryVariables,
  AdminReSyncFinancialDataMutation,
  AdminReSyncFinancialDataMutationVariables,
  AdminSetFinancialAccountIgnoredMutation,
  AdminSetFinancialAccountIgnoredMutationVariables,
  AdminSyncInvestmentHoldingsMutation,
  AdminSyncInvestmentHoldingsMutationVariables,
  AdminSyncInvestmentTransactionsMutation,
  AdminSyncInvestmentTransactionsMutationVariables,
} from './__generated__/BankConnectionsScene.gql';

const ADMIN_GET_FAMILY_BANK_CONNECTIONS = gql`
  query AdminGetFamilyBankConnections($familyId: ID!) {
    adminGetFamilyBankConnections(familyId: $familyId) {
      id
      externalId
      lastSyncedAt
      bank {
        id
        name
        logoUrl
      }
      user {
        id
        email
      }
      accounts {
        id
        name
        mask
        type
        subtype
        ignoredAt
      }
    }
    adminGetBankConnectionsHealth(familyId: $familyId) {
      bankConnectionId
      stale
      neverSynced
      plaidJSON
      error
      consentedProducts
      lastHoldingsSyncAt
      investmentTransactionsSyncedAt
    }
  }
`;

// Its own document rather than a field on the connections query: a failure
// here must not blank the connections (and their Resync / Delete) with it.
const ADMIN_GET_FINANCIAL_ACCOUNT_DETAILS = gql`
  query AdminGetFinancialAccountDetails($familyId: ID!) {
    adminGetFinancialAccountDetails(familyId: $familyId) {
      financialAccountId
      currencyCode
      currentBalance
      availableBalance
      creditLimit
      balanceAsOf
      investmentAudit {
        relinkCandidate
        holdingsValue
        balanceGap
        positionCount
        positionsMissingPrice
        positionsMissingCostBasis
        positionsWithUnidentifiedSecurity
        positionsQuantityChangedWithoutPriceChange
        positions {
          isoCurrencyCode
          unofficialCurrencyCode
        }
      }
    }
  }
`;

const ADMIN_SYNC_INVESTMENT_HOLDINGS = gql`
  mutation AdminSyncInvestmentHoldings($bankConnectionId: ID!) {
    adminSyncInvestmentHoldings(bankConnectionId: $bankConnectionId) {
      bankConnectionId
      outcome
      providerErrorCode
      accountsSynced
      holdingsRecorded
      consentedProducts
      lastHoldingsSyncAt
    }
  }
`;

const ADMIN_SYNC_INVESTMENT_TRANSACTIONS = gql`
  mutation AdminSyncInvestmentTransactions($bankConnectionId: ID!) {
    adminSyncInvestmentTransactions(bankConnectionId: $bankConnectionId) {
      bankConnectionId
      outcome
    }
  }
`;

const ADMIN_RESYNC_FINANCIAL_DATA = gql`
  mutation AdminReSyncFinancialData($bankConnectionId: ID!, $resetCursor: Boolean) {
    adminReSyncFinancialData(bankConnectionId: $bankConnectionId, resetCursor: $resetCursor) {
      id
      lastSyncedAt
    }
  }
`;

const ADMIN_DELETE_BANK_CONNECTION = gql`
  mutation AdminDeleteBankConnection($bankConnectionId: ID!) {
    adminDeleteBankConnection(bankConnectionId: $bankConnectionId)
  }
`;

const ADMIN_SET_FINANCIAL_ACCOUNT_IGNORED = gql`
  mutation AdminSetFinancialAccountIgnored($financialAccountId: ID!, $ignored: Boolean!) {
    adminSetFinancialAccountIgnored(financialAccountId: $financialAccountId, ignored: $ignored) {
      id
      ignoredAt
    }
  }
`;

const formatLastSynced = (date: string | null | undefined): string => {
  if (!date) {
    return 'Never';
  }
  return new Date(date).toLocaleString();
};

/**
 * Bank Connections admin tool. Operators select a user, then view every Plaid
 * bank connection on their family with sync health (stale / never-synced /
 * Plaid item JSON), can resync the Plaid pipeline (with or without resetting
 * the transaction cursor), can pull a connection's investment transactions
 * behind a confirmation that names the subscription behind it, and can delete
 * connections (revoking the Plaid access token first). Each connection's accounts expand into a table of
 * newest balances and holdings data-quality flags, with a per-account detail
 * panel; account figures load separately, so the page works without them.
 * The selector is user-keyed for operator UX, but
 * queries and mutations send `familyId` over the wire — the data is family
 * scoped (see ORN-526). A `?userId=` link opens with that user selected.
 */
const BankConnectionsScene = () => {
  const [selection, setSelection] = React.useState<IAdminSelectUserSelection | null>(null);
  useSeedSelectionFromLink(setSelection);
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
  const [bankConnectionToDelete, setBankConnectionToDelete] = React.useState<string | null>(null);
  const [actionLoading, setActionLoading] = React.useState<string | null>(null);
  const [expandedAccounts, setExpandedAccounts] = React.useState<Set<string>>(new Set());
  const [expandedHealthJSON, setExpandedHealthJSON] = React.useState<Set<string>>(new Set());
  const [accountToSuppress, setAccountToSuppress] = React.useState<{ id: string; name: string } | null>(null);
  const [connectionToPull, setConnectionToPull] = React.useState<{ id: string; bankName: string } | null>(null);

  const { showToast } = useToast();

  const [
    loadBankConnections,
    { data: bankConnectionsData, loading: bankConnectionsLoading, refetch: refetchConnections },
  ] = useLazyQuery<AdminGetFamilyBankConnectionsQuery, AdminGetFamilyBankConnectionsQueryVariables>(
    ADMIN_GET_FAMILY_BANK_CONNECTIONS
  );

  const {
    data: accountDetailsData,
    error: accountDetailsError,
    refetch: refetchAccountDetails,
  } = useQuery<AdminGetFinancialAccountDetailsQuery, AdminGetFinancialAccountDetailsQueryVariables>(
    ADMIN_GET_FINANCIAL_ACCOUNT_DETAILS,
    { variables: { familyId: selection?.familyId ?? '' }, skip: !selection?.familyId }
  );

  const [resyncFinancialData] = useMutation<
    AdminReSyncFinancialDataMutation,
    AdminReSyncFinancialDataMutationVariables
  >(ADMIN_RESYNC_FINANCIAL_DATA);

  const [syncInvestmentHoldings] = useMutation<
    AdminSyncInvestmentHoldingsMutation,
    AdminSyncInvestmentHoldingsMutationVariables
  >(ADMIN_SYNC_INVESTMENT_HOLDINGS);

  const [syncInvestmentTransactions] = useMutation<
    AdminSyncInvestmentTransactionsMutation,
    AdminSyncInvestmentTransactionsMutationVariables
  >(ADMIN_SYNC_INVESTMENT_TRANSACTIONS);

  const [deleteBankConnection] = useMutation<
    AdminDeleteBankConnectionMutation,
    AdminDeleteBankConnectionMutationVariables
  >(ADMIN_DELETE_BANK_CONNECTION);

  const [setFinancialAccountIgnored] = useMutation<
    AdminSetFinancialAccountIgnoredMutation,
    AdminSetFinancialAccountIgnoredMutationVariables
  >(ADMIN_SET_FINANCIAL_ACCOUNT_IGNORED);

  const familyId = selection?.familyId ?? null;

  React.useEffect(() => {
    if (familyId) {
      void loadBankConnections({ variables: { familyId } });
    }
  }, [familyId, loadBankConnections]);

  const healthStatusMap = React.useMemo(() => {
    const map = new Map<
      string,
      NonNullable<AdminGetFamilyBankConnectionsQuery['adminGetBankConnectionsHealth']>[number]
    >();
    bankConnectionsData?.adminGetBankConnectionsHealth?.forEach((status) => {
      map.set(status.bankConnectionId, status);
    });
    return map;
  }, [bankConnectionsData]);

  const accountDetailsById = React.useMemo(() => {
    const map = new Map<string, AccountDetailRow>();
    accountDetailsData?.adminGetFinancialAccountDetails.forEach((detail) => {
      map.set(detail.financialAccountId, detail);
    });
    return map;
  }, [accountDetailsData]);

  // A details failure is reported by the Apollo errorLink and shown inline; it must not fail the action that refetched.
  const refetchAll = async () => {
    await Promise.all([refetchConnections(), refetchAccountDetails().catch(() => undefined)]);
  };

  const handleUserSelect = (next: IAdminSelectUserSelection | null) => {
    setSelection(next);
    setDeleteModalOpen(false);
    setBankConnectionToDelete(null);
    setAccountToSuppress(null);
    setConnectionToPull(null);
    setExpandedHealthJSON(new Set());
    setExpandedAccounts(new Set());
  };

  const toggleHealthJSONExpanded = (bankConnectionId: string) => {
    setExpandedHealthJSON((prev) => {
      const next = new Set(prev);
      if (next.has(bankConnectionId)) {
        next.delete(bankConnectionId);
      } else {
        next.add(bankConnectionId);
      }
      return next;
    });
  };

  const toggleAccountsExpanded = (bankConnectionId: string) => {
    setExpandedAccounts((prev) => {
      const next = new Set(prev);
      if (next.has(bankConnectionId)) {
        next.delete(bankConnectionId);
      } else {
        next.add(bankConnectionId);
      }
      return next;
    });
  };

  const runResync = async (bankConnectionId: string, resetCursor: boolean) => {
    if (!familyId) {
      showToast({ type: 'error', message: 'Select a user before resyncing.' });
      return;
    }
    setActionLoading(bankConnectionId);
    try {
      await resyncFinancialData({
        variables: { bankConnectionId, resetCursor },
        refetchQueries: [ADMIN_GET_FINANCIAL_ACCOUNT_DETAIL_OPERATION],
      });
      await refetchAll();
      showToast({
        type: 'success',
        message: resetCursor ? 'Resync complete (cursor reset).' : 'Resync complete.',
      });
    } catch {
      // ApolloProvider's errorLink reports GraphQL + network errors to Sentry.
    } finally {
      setActionLoading(null);
    }
  };

  const runSyncHoldings = async (bankConnectionId: string) => {
    setActionLoading(bankConnectionId);
    try {
      const { data } = await syncInvestmentHoldings({
        variables: { bankConnectionId },
        refetchQueries: [ADMIN_GET_FINANCIAL_ACCOUNT_DETAIL_OPERATION],
      });
      if (data) {
        showToast(describeHoldingsSyncOutcome(data.adminSyncInvestmentHoldings));
      }
      await refetchAll();
    } catch {
      // ApolloProvider's errorLink reports GraphQL + network errors to Sentry.
    } finally {
      setActionLoading(null);
    }
  };

  const handlePullConfirm = async () => {
    if (!connectionToPull || actionLoading) {
      return;
    }
    const { id } = connectionToPull;
    setConnectionToPull(null);
    setActionLoading(id);
    try {
      const { data } = await syncInvestmentTransactions({ variables: { bankConnectionId: id } });
      if (data) {
        showToast(describeInvestmentTransactionsSyncOutcome(data.adminSyncInvestmentTransactions.outcome));
      }
    } catch {
      // A request that timed out may still have handed the pull off, so the
      // operator is told to look before asking again.
      showToast({ type: 'error', message: INVESTMENT_TRANSACTIONS_REQUEST_FAILED_MESSAGE });
      setActionLoading(null);
      return;
    }
    // The request has answered by here, so a failed refresh must not read as a failed request.
    await refetchAll().catch(() => undefined);
    setActionLoading(null);
  };

  const handleSuppressConfirm = async () => {
    if (!accountToSuppress) {
      return;
    }
    const { id } = accountToSuppress;
    setAccountToSuppress(null);
    await runSetAccountIgnored(id, true);
  };

  const runSetAccountIgnored = async (financialAccountId: string, ignored: boolean) => {
    setActionLoading(financialAccountId);
    try {
      await setFinancialAccountIgnored({ variables: { financialAccountId, ignored } });
      await refetchAll();
      showToast({
        type: 'success',
        message: ignored
          ? 'Account suppressed — hidden from the family.'
          : 'Account restored — visible to the family again.',
      });
    } catch {
      // ApolloProvider's errorLink reports GraphQL + network errors to Sentry.
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteClick = (bankConnectionId: string) => {
    setBankConnectionToDelete(bankConnectionId);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!bankConnectionToDelete || !familyId) {
      return;
    }

    setActionLoading(bankConnectionToDelete);
    try {
      await deleteBankConnection({
        variables: { bankConnectionId: bankConnectionToDelete },
      });
      setDeleteModalOpen(false);
      setBankConnectionToDelete(null);
      await refetchAll();
      showToast({ type: 'success', message: 'Bank connection deleted.' });
    } catch {
      // ApolloProvider's errorLink reports GraphQL + network errors to Sentry.
    } finally {
      setActionLoading(null);
    }
  };

  const selectedUserEmail = bankConnectionsData?.adminGetFamilyBankConnections?.[0]?.user?.email ?? null;

  return (
    <div className="bg-bg-primary text-fg-primary flex min-h-screen flex-col">
      <Header />
      <main className="flex flex-1 flex-col gap-6 px-6 py-5">
        <div className="flex flex-col gap-1">
          <h1 className="text-sm font-semibold uppercase tracking-widest">Bank connections</h1>
          <p className="text-fg-secondary text-xs">
            Select a user to inspect their family's Plaid bank connections, force a resync, or revoke + delete.
          </p>
        </div>

        <div className="w-full max-w-md">
          <AdminSelectUser selectedUserId={selection?.userId ?? null} onSelectionChange={handleUserSelect} />
        </div>

        {!familyId ? (
          <div className="text-fg-tertiary text-xs italic">Select a user to view bank connections.</div>
        ) : bankConnectionsLoading ? (
          <div className="text-fg-secondary text-xs">Loading bank connections…</div>
        ) : !bankConnectionsData?.adminGetFamilyBankConnections?.length ? (
          <div className="text-fg-tertiary text-xs italic">No bank connections found for this family.</div>
        ) : (
          <div className="flex flex-col gap-2">
            <div className="text-fg-secondary text-xs">
              {selectedUserEmail ? (
                <>
                  Bank connections for family of <span className="font-mono">{selectedUserEmail}</span>
                </>
              ) : (
                'Bank connections'
              )}
            </div>

            {bankConnectionsData.adminGetFamilyBankConnections.map((bankConnection) => {
              const healthStatus = healthStatusMap.get(bankConnection.id);
              const isAccountsExpanded = expandedAccounts.has(bankConnection.id);
              const isHealthExpanded = expandedHealthJSON.has(bankConnection.id);
              const isActing = actionLoading === bankConnection.id;
              const holdsInvestments = hasInvestmentAccount(bankConnection.accounts);
              const consentedProducts = healthStatus?.consentedProducts ?? null;
              const isRelinkCandidate = isInvestmentsRelinkCandidate(bankConnection.accounts, consentedProducts);

              const statusLabel = healthStatus
                ? healthStatus.error
                  ? 'Error'
                  : healthStatus.neverSynced
                    ? 'Never synced'
                    : healthStatus.stale
                      ? 'Stale'
                      : 'Healthy'
                : null;
              const statusBadgeClass = healthStatus
                ? healthStatus.error
                  ? 'bg-error-primary text-fg-error-primary'
                  : healthStatus.neverSynced || healthStatus.stale
                    ? 'bg-warning-primary text-fg-warning-primary'
                    : 'bg-success-primary text-fg-success-primary'
                : '';

              return (
                <div key={bankConnection.id} className="border-border-primary bg-bg-primary border px-3 py-2">
                  <div className="flex items-center gap-3">
                    {bankConnection.bank.logoUrl && (
                      <img
                        src={bankConnection.bank.logoUrl}
                        alt={bankConnection.bank.name}
                        className="border-border-primary h-6 w-6 shrink-0 border"
                      />
                    )}
                    <div className="flex items-baseline gap-2 text-sm font-semibold">
                      <span>{bankConnection.bank.name}</span>
                      {statusLabel && (
                        <span
                          className={`inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider ${statusBadgeClass}`}
                        >
                          {statusLabel}
                        </span>
                      )}
                      {isRelinkCandidate && (
                        <span className="bg-warning-primary text-fg-warning-primary inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider">
                          No investments consent
                        </span>
                      )}
                      {healthStatus && (
                        <span className="text-fg-tertiary flex items-center gap-1 font-mono text-[11px] font-normal">
                          {consentedProducts === null ? (
                            <span className="text-fg-quaternary">consent unread</span>
                          ) : consentedProducts.length === 0 ? (
                            <span className="text-fg-quaternary">no consented products</span>
                          ) : (
                            consentedProducts.map((product, index) => (
                              <React.Fragment key={product}>
                                {index > 0 && <span className="text-fg-quaternary">·</span>}
                                <span className={product === INVESTMENTS_PRODUCT ? 'text-fg-brand-primary' : undefined}>
                                  {product}
                                </span>
                              </React.Fragment>
                            ))
                          )}
                        </span>
                      )}
                    </div>
                    <div className="ml-auto flex shrink-0 items-center gap-1.5">
                      <Button
                        onClick={() => void runResync(bankConnection.id, false)}
                        isDisabled={isActing}
                        isLoading={isActing}
                        color="secondary"
                        size="xs"
                      >
                        Resync
                      </Button>
                      <Button
                        onClick={() => void runResync(bankConnection.id, true)}
                        isDisabled={isActing}
                        isLoading={isActing}
                        color="secondary"
                        size="xs"
                      >
                        Resync · reset cursor
                      </Button>
                      <Button
                        onClick={() => void runSyncHoldings(bankConnection.id)}
                        isDisabled={isActing}
                        isLoading={isActing}
                        color="secondary"
                        size="xs"
                      >
                        Sync holdings
                      </Button>
                      <Button
                        onClick={() =>
                          setConnectionToPull({
                            id: bankConnection.id,
                            bankName: bankConnection.bank.name,
                          })
                        }
                        isDisabled={isActing || !holdsInvestments}
                        isLoading={isActing}
                        color="secondary"
                        size="xs"
                      >
                        Sync investment transactions
                      </Button>
                      <Button
                        onClick={() => handleDeleteClick(bankConnection.id)}
                        isDisabled={isActing}
                        color="danger"
                        size="xs"
                      >
                        Delete
                      </Button>
                    </div>
                  </div>

                  <div className="text-fg-tertiary mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 font-mono text-[11px]">
                    <span>
                      Synced {bankConnection.lastSyncedAt ? formatTimeAgo(bankConnection.lastSyncedAt) : 'never'}
                      {bankConnection.lastSyncedAt && (
                        <span className="text-fg-quaternary"> · {formatLastSynced(bankConnection.lastSyncedAt)}</span>
                      )}
                    </span>
                    {holdsInvestments && healthStatus && (
                      <span>
                        Holdings synced{' '}
                        {healthStatus.lastHoldingsSyncAt ? formatTimeAgo(healthStatus.lastHoldingsSyncAt) : 'never'}
                        {healthStatus.lastHoldingsSyncAt && (
                          <span className="text-fg-quaternary">
                            {' '}
                            · {formatLastSynced(healthStatus.lastHoldingsSyncAt)}
                          </span>
                        )}
                      </span>
                    )}
                    {holdsInvestments && healthStatus && (
                      <span>
                        Transactions synced{' '}
                        {healthStatus.investmentTransactionsSyncedAt
                          ? formatTimeAgo(healthStatus.investmentTransactionsSyncedAt)
                          : 'never'}
                        {healthStatus.investmentTransactionsSyncedAt && (
                          <span className="text-fg-quaternary">
                            {' '}
                            · {formatLastSynced(healthStatus.investmentTransactionsSyncedAt)}
                          </span>
                        )}
                      </span>
                    )}
                    <span>
                      <span className="text-fg-quaternary">plaid </span>
                      {bankConnection.externalId}
                    </span>
                    <span>
                      <span className="text-fg-quaternary">conn </span>
                      {bankConnection.id}
                    </span>
                  </div>

                  <div className="mt-1.5 flex items-center gap-3 text-xs">
                    {healthStatus && (
                      <button
                        type="button"
                        onClick={() => toggleHealthJSONExpanded(bankConnection.id)}
                        className="text-fg-secondary hover:text-fg-primary inline-flex items-center gap-1"
                      >
                        <span>Health JSON</span>
                        {isHealthExpanded ? (
                          <ChevronUpIcon className="size-3 shrink-0" />
                        ) : (
                          <ChevronDownIcon className="size-3 shrink-0" />
                        )}
                      </button>
                    )}
                    {bankConnection.accounts.length > 0 && (
                      <button
                        type="button"
                        onClick={() => toggleAccountsExpanded(bankConnection.id)}
                        className="text-fg-secondary hover:text-fg-primary inline-flex items-center gap-1"
                      >
                        <span>Accounts ({bankConnection.accounts.length})</span>
                        {isAccountsExpanded ? (
                          <ChevronUpIcon className="size-3 shrink-0" />
                        ) : (
                          <ChevronDownIcon className="size-3 shrink-0" />
                        )}
                      </button>
                    )}
                    {healthStatus?.error && (
                      <span className="text-fg-error-primary truncate">{healthStatus.error}</span>
                    )}
                  </div>

                  {isHealthExpanded && healthStatus && (
                    <pre className="bg-bg-secondary text-fg-primary mt-1.5 max-h-64 overflow-auto p-2 font-mono text-[10px] leading-snug">
                      {healthStatus.plaidJSON ? JSON.stringify(healthStatus.plaidJSON, null, 2) : 'No data'}
                    </pre>
                  )}

                  {isAccountsExpanded && bankConnection.accounts.length > 0 && (
                    <>
                      {accountDetailsError && (
                        <div className="text-fg-error-primary mt-1.5 text-xs">
                          Account figures could not be loaded — the rows below show none.
                        </div>
                      )}
                      <AccountsTable
                        familyId={familyId}
                        accounts={bankConnection.accounts}
                        detailsById={accountDetailsById}
                        actionLoadingId={actionLoading}
                        onSuppress={setAccountToSuppress}
                        onRestore={(financialAccountId) => void runSetAccountIgnored(financialAccountId, false)}
                      />
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <Modal
          isOpen={accountToSuppress !== null}
          onOpenChange={(open) => !open && setAccountToSuppress(null)}
          size="md"
        >
          <Modal.Header onClose={() => setAccountToSuppress(null)}>Suppress account</Modal.Header>
          <Modal.Body>
            <div className="flex flex-col gap-4">
              <div className="text-fg-primary flex flex-col gap-2 text-sm">
                <div>
                  Hide <span className="font-mono">{accountToSuppress?.name}</span> and all of its transactions from the
                  family's app?
                </div>
                <div className="text-fg-secondary text-xs">
                  Bill payments made from this account are unlinked, so bills they paid will show unpaid — restoring the
                  account does not re-link them. The account keeps syncing and can be restored here at any time.
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button onClick={() => setAccountToSuppress(null)} color="secondary" size="sm">
                  Cancel
                </Button>
                <Button
                  onClick={() => void handleSuppressConfirm()}
                  isDisabled={Boolean(actionLoading)}
                  isLoading={Boolean(actionLoading)}
                  color="danger"
                  size="sm"
                >
                  Suppress account
                </Button>
              </div>
            </div>
          </Modal.Body>
        </Modal>

        <Modal isOpen={connectionToPull !== null} onOpenChange={(open) => !open && setConnectionToPull(null)} size="md">
          <Modal.Header onClose={() => setConnectionToPull(null)}>Sync investment transactions</Modal.Header>
          <Modal.Body>
            <div className="flex flex-col gap-4">
              <div className="text-fg-primary flex flex-col gap-2 text-sm">
                <div>
                  Pull the last 24 months of investment transactions for{' '}
                  <span className="font-mono">{connectionToPull?.bankName}</span>?
                </div>
                <div className="text-fg-secondary text-xs">
                  A connection is pulled when it is linked, which starts its Plaid Investments Transactions
                  subscription. If this one has not been pulled yet, this pull starts it. The subscription is billed
                  until the connection is deleted and cannot be stopped any other way.
                </div>
                <div className="text-fg-secondary text-xs">
                  A balance sync and the billing checks run first; a connection that fails one is refused and nothing is
                  fetched. The pull is then handed to a background worker and can take a few minutes.
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button onClick={() => setConnectionToPull(null)} color="secondary" size="sm">
                  Cancel
                </Button>
                <Button
                  onClick={() => void handlePullConfirm()}
                  isDisabled={Boolean(actionLoading)}
                  isLoading={Boolean(actionLoading)}
                  color="primary"
                  size="sm"
                >
                  Pull transactions
                </Button>
              </div>
            </div>
          </Modal.Body>
        </Modal>

        <Modal isOpen={deleteModalOpen} onOpenChange={setDeleteModalOpen} size="md">
          <Modal.Header onClose={() => setDeleteModalOpen(false)}>Delete bank connection</Modal.Header>
          <Modal.Body>
            <div className="flex flex-col gap-4">
              <div className="text-fg-primary flex flex-col gap-2 text-sm">
                <div>Are you sure? This action cannot be undone.</div>
                <div>The connection will be revoked from Plaid and removed from the database.</div>
                {selectedUserEmail && (
                  <div className="text-fg-secondary text-xs">
                    The owner will need to log in and re-add the bank to restore this information; some historical data
                    may still be lost.
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <Button onClick={() => setDeleteModalOpen(false)} color="secondary" size="sm">
                  Cancel
                </Button>
                <Button
                  onClick={() => void handleDeleteConfirm()}
                  isDisabled={Boolean(actionLoading)}
                  isLoading={Boolean(actionLoading)}
                  color="danger"
                  size="sm"
                >
                  Delete
                </Button>
              </div>
            </div>
          </Modal.Body>
        </Modal>
      </main>
    </div>
  );
};

export default BankConnectionsScene;
